import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import {
  Unsubscribe} from '@angular/fire/firestore';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@core/services/infra/logger.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceType } from '@shared/definitions/workspace';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { ProductAnalyticsService } from '@core/services/infra/product-analytics.service';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import { sanitizeRichContentBlocks } from '@core/services/ai/rich-content.guard';
import { environment } from '../../../../environments/environment';

/**
 * Workspace types where the assistant entry is exposed in the UI.
 *
 * This list is a PRODUCT decision, not a mirror of the backend catalogue. A
 * type having tools is a necessary condition, never a sufficient one — the
 * backend gate (`chatAssistant.ts` rejects when the filtered catalogue is
 * empty) is what a type must clear to work at all; this list is what it must
 * clear to be OFFERED.
 *
 * `tenant` is deliberately absent. `ai/tools/tenant.tools.ts` still serves four
 * tenant tools and `help.tools.ts` still accepts the type, so the callable
 * would answer — tenants are simply not given a door to it. Removing the type
 * here turns off all four entry points at once, since each is gated on
 * `isAvailable()`: the tenant header button, the tenant side rail, the
 * Assistant tab of the docked panel, and the Vex sidenav link. The panel itself
 * stays mounted in the tenant shell: it is also how support is reached, and
 * with no assistant it opens straight on the Support tab — the path
 * `service_provider` and `syndic` already take.
 *
 * `syndic` and `service_provider` were dropped in 2026-05 for the opposite
 * reason to tenant: no tools at all, so the door would have opened on an empty
 * room. Each now has a catalogue of its own (`ai/tools/syndic.tools.ts`,
 * `ai/tools/provider.tools.ts`), which is why they are added here — the
 * necessary condition is finally met, and offering it is the product call this
 * list records.
 *
 * The order matters and is worth stating once: this list is opened LAST, after
 * the tools exist. Opening it first would give a syndic an assistant that
 * refuses every message, since the backend gate rejects an empty catalogue.
 */
const CHAT_ENABLED_WORKSPACE_TYPES: ReadonlySet<WorkspaceType> = new Set<WorkspaceType>([
  'personal',
  'professional',
  'business',
  'agency',
  'syndic',
  'service_provider'
]);

export interface ChatPageContext {
  route?: string;
  focusedEntityType?: string;
  focusedEntityId?: string;
  /** Active tab/sub-view within the focused page, e.g. 'repairs'. */
  tab?: string;
  /** Active list filters — the backend drops any key it doesn't allowlist. */
  filters?: Record<string, string | number | boolean>;
  /** Entity ids visible in the current list; the backend caps this at 20. */
  visibleEntityIds?: string[];
  /** Id of the cockpit card currently open, when `focusedEntityType === 'cockpit'`. */
  cockpitCardId?: string;
}

export type ChatMessageRole = 'user' | 'assistant' | 'tool';

export type PendingActionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'failed';

export interface PendingActionEmbed {
  actionId: string;
  toolName: string;
  summary: string;
  riskLevel: 'low' | 'medium' | 'high';
  status: PendingActionStatus;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  firstUserMessage: string | null;
  lastMessageAt: Date | null;
  updatedAt: Date | null;
  pinned: boolean;
  temporary: boolean;
}

/**
 * Structured payloads the assistant can attach to a message so the UI
 * can render rich, contextual widgets instead of long markdown bodies.
 * Mirrors `RichContentBlock` in `backend/functions/src/ai/tools/types.ts`
 * — keep the two definitions in sync.
 *
 * Versioning: never change the meaning of an existing field silently.
 * Add an optional field, or bump to a new `kind`. The dispatcher falls
 * back to text rendering when it doesn't recognise a kind, so old
 * clients keep working when the backend ships a new block type.
 */
export type RichContentEntityType =
  | 'property'
  | 'contract'
  | 'tenant'
  | 'loan'
  | 'expense'
  | 'intervention'
  | 'document'
  | 'owner'
  | 'finance'
  | 'cockpit';

export type RichContentBlock =
  | EntityMatchBlock
  | EntitySummaryBlock
  | FormFieldsBlock
  | ChoiceBlock
  | SummaryBlock
  | KpiGridBlock
  | EntityListBlock
  | DataTableBlock
  | TimelineBlock
  | QuestionBlock
  | BarChartBlock
  | DonutChartBlock
  | StatusTrackerBlock
  | AlertBlock
  | SpecListBlock
  | MetricCardBlock
  | StatComparisonBlock
  | EntityPickBlock
  | QuoteBlock
  | ProgressBarBlock
  | AddressCardBlock
  | CodeBlock
  | NavigateActionBlock
  | NavigateListBlock;

/**
 * Mirror of `NavigateActionBlock` in the backend types. Renders as a
 * single in-app deeplink CTA so the assistant can hand the user off to
 * the right form (create a lease, add a tenant, …) without trying to
 * stage the create itself.
 */
export interface NavigateActionBlock {
  kind: 'navigate-action';
  label: string;
  route: string;
  description?: string;
  icon?: string;
  queryParams?: Record<string, string | number | boolean>;
  tone?: WidgetTone;
  entityType?: RichContentEntityType;
  /** ngx-translate key — overrides `label` when present + resolvable. */
  labelKey?: string;
  /** ngx-translate key — overrides `description` when present + resolvable. */
  descriptionKey?: string;
}

/**
 * Multiple CTAs side-by-side, used to surface a small set of related
 * navigation options ("what can I add?", "where do I manage X?").
 */
export interface NavigateListBlock {
  kind: 'navigate-list';
  title?: string;
  titleKey?: string;
  items: readonly NavigateListItem[];
}

export interface NavigateListItem {
  id: string;
  label: string;
  route: string;
  description?: string;
  icon?: string;
  queryParams?: Record<string, string | number | boolean>;
  tone?: WidgetTone;
  entityType?: RichContentEntityType;
  labelKey?: string;
  descriptionKey?: string;
}

export interface ProgressBarBlock {
  kind: 'progress-bar';
  title?: string;
  label: string;
  percent: number;
  valueLabel?: string;
  tone?: WidgetTone;
  hint?: string;
}

export interface AddressCardBlock {
  kind: 'address-card';
  title?: string;
  name?: string;
  address: string;
  fields?: readonly { label: string; value: string }[];
  contacts?: readonly {
    kind: 'phone' | 'email' | 'url';
    label: string;
    value: string;
  }[];
}

export interface CodeBlock {
  kind: 'code-block';
  title?: string;
  code: string;
  language?: string;
}

export interface BarChartBlock {
  kind: 'bar-chart';
  title?: string;
  bars: readonly {
    label: string;
    value: number;
    valueLabel?: string;
    tone?: WidgetTone;
  }[];
  caption?: string;
}

export interface DonutChartBlock {
  kind: 'donut-chart';
  title?: string;
  slices: readonly {
    label: string;
    value: number;
    color?: string;
  }[];
  centerValue?: string;
  centerLabel?: string;
  caption?: string;
}

export interface StatusTrackerBlock {
  kind: 'status-tracker';
  title?: string;
  currentIndex: number;
  steps: readonly {
    label: string;
    sub?: string;
    icon?: string;
  }[];
  tone?: WidgetTone;
}

export interface AlertBlock {
  kind: 'alert';
  severity: 'info' | 'success' | 'warning' | 'danger';
  title: string;
  body?: string;
  icon?: string;
  /**
   * Optional CTA. `route` (in-app navigation) takes precedence over
   * `message` (re-inject as a new user turn) when both are present.
   */
  action?: {
    label: string;
    message?: string;
    route?: string;
    queryParams?: Record<string, string | number | boolean>;
    labelKey?: string;
  };
}

export interface SpecListBlock {
  kind: 'spec-list';
  title?: string;
  specs: readonly {
    icon: string;
    label: string;
    value: string;
  }[];
}

export interface MetricCardBlock {
  kind: 'metric-card';
  label: string;
  value: string;
  icon?: string;
  tone?: WidgetTone;
  trend?: {
    direction: 'up' | 'down' | 'flat';
    label: string;
    tone?: WidgetTone;
  };
  hint?: string;
}

export interface StatComparisonBlock {
  kind: 'stat-comparison';
  title?: string;
  separator?: string;
  left: ComparisonSide;
  right: ComparisonSide;
}

export interface ComparisonSide {
  label: string;
  value: string;
  sub?: string;
  icon?: string;
  tone?: WidgetTone;
}

export interface EntityPickBlock {
  kind: 'entity-pick';
  title?: string;
  prompt?: string;
  options: readonly {
    id: string;
    entityType: RichContentEntityType;
    entityId: string;
    title: string;
    subtitle?: string;
    icon?: string;
    badge?: { label: string; tone: WidgetTone };
    message?: string;
  }[];
}

export interface QuoteBlock {
  kind: 'quote';
  text: string;
  attribution?: string;
  icon?: string;
}

export type WidgetTone = 'neutral' | 'positive' | 'warning' | 'danger';

export interface KpiGridBlock {
  kind: 'kpi-grid';
  title?: string;
  items: readonly {
    label: string;
    value: string | number;
    icon?: string;
    tone?: WidgetTone;
    hint?: string;
  }[];
}

export interface EntityListBlock {
  kind: 'entity-list';
  title?: string;
  items: readonly {
    id: string;
    entityType: RichContentEntityType;
    title: string;
    subtitle?: string;
    amount?: { value: number; currency: string };
    badge?: { label: string; tone: WidgetTone };
    icon?: string;
    /**
     * Explicit in-app detail route set by the emitting tool. Preferred over
     * the entityType-based guess (which can't tell repairs from maintenance
     * or providers from workspace members). Must start with "/".
     */
    route?: string;
  }[];
  caption?: string;
  /** "See all →" deep-link to the full list page (must start with "/"). */
  seeAllRoute?: string;
  seeAllQueryParams?: Record<string, string | number | boolean>;
  seeAllLabelKey?: string;
  /** Inline rows shown before collapsing the rest behind "Show N more". */
  maxVisible?: number;
}

export interface DataTableBlock {
  kind: 'data-table';
  title?: string;
  columns: readonly {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
  }[];
  rows: readonly {
    id: string;
    cells: Record<string, string | number>;
    tone?: WidgetTone;
    /** Optional in-app deeplink for the row "Open" CTA. */
    route?: string;
    entityType?: RichContentEntityType;
    /** When set with tenantId, UI can stage a payment-reminder confirm. */
    tenantId?: string;
    contractId?: string;
    rowAction?: 'remind';
  }[];
  totals?: Record<string, string | number>;
  caption?: string;
  /** "See all →" deep-link to the full page (must start with "/"). */
  seeAllRoute?: string;
  seeAllQueryParams?: Record<string, string | number | boolean>;
  seeAllLabelKey?: string;
}

export interface TimelineBlock {
  kind: 'timeline';
  title?: string;
  events: readonly {
    date: string;
    label: string;
    description?: string;
    icon?: string;
    tone?: WidgetTone;
  }[];
}

export interface QuestionBlock {
  kind: 'question';
  prompt: string;
  placeholder?: string;
  submitLabel?: string;
  helperText?: string;
}

export interface EntityMatchBlock {
  kind: 'entity-match';
  entityType: RichContentEntityType;
  entityId: string;
  title: string;
  subtitle?: string;
  icon?: string;
  meta?: readonly { label: string; value: string }[];
}

export interface EntitySummaryBlock {
  kind: 'entity-summary';
  title: string;
  icon?: string;
  fields: readonly { label: string; value: string; emphasis?: boolean }[];
  caption?: string;
}

export interface FormFieldsBlock {
  kind: 'form-fields';
  formId: string;
  toolName?: string;
  title?: string;
  knownArgs?: Record<string, unknown>;
  fields: readonly FormFieldDefinition[];
  submitLabel?: string;
}

export type FormFieldDefinition =
  | {
      name: string;
      label: string;
      kind: 'text' | 'number' | 'date';
      required?: boolean;
      placeholder?: string;
      defaultValue?: string | number;
      helperText?: string;
    }
  | {
      name: string;
      label: string;
      kind: 'boolean';
      required?: boolean;
      defaultValue?: boolean;
      helperText?: string;
    }
  | {
      name: string;
      label: string;
      kind: 'select';
      required?: boolean;
      options: readonly { value: string; label: string }[];
      defaultValue?: string;
      helperText?: string;
    };

export interface ChoiceBlock {
  kind: 'choice';
  prompt: string;
  choices: readonly ChoiceOption[];
}

export interface ChoiceOption {
  id: string;
  label: string;
  description?: string;
  /**
   * When set, clicking the choice sends this text as a new user
   * message — the simplest way to nudge the model down a branch.
   */
  message?: string;
}

export interface SummaryBlock {
  kind: 'summary';
  title: string;
  bullets: readonly string[];
  icon?: string;
  tone?: 'info' | 'success' | 'warning';
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  content: string;
  createdAt: Date | null;
  modelUsed?: string;
  toolsUsed?: string[];
  pendingActions?: PendingActionEmbed[];
  richContent?: RichContentBlock[];
  /** True while the backend is still streaming tokens into this message. */
  streaming?: boolean;
  /**
   * Tool names the backend is executing RIGHT NOW for this reply (set
   * during tool rounds, cleared by the final authoritative write). Lets
   * the panel say "checking your data…" instead of mute typing dots.
   */
  toolsInProgress?: string[];
}

export interface ChatAttachmentRef {
  name: string;
  mimeType: string;
  storagePath: string;
  sizeBytes: number;
}

interface SendMessageRequest {
  workspaceId: string;
  /** UI language (fr | en | nl | de) so the assistant answers and renders widgets in it. */
  language?: string;
  message: string;
  conversationId?: string;
  pageContext?: ChatPageContext;
  temporary?: boolean;
  attachments?: ChatAttachmentRef[];
}

interface SendMessageResponse {
  conversationId: string;
  assistantMessageId: string;
  reply: string;
  modelUsed: string;
  toolsUsed: string[];
  pendingActions: PendingActionEmbed[];
}

interface ConfirmActionRequest {
  workspaceId: string;
  language?: string;
  actionId: string;
  decision: 'confirm' | 'reject';
}

interface ConfirmActionResponse {
  actionId: string;
  status: 'approved' | 'rejected' | 'failed' | 'expired';
  result?: unknown;
  errorMessage?: string;
}

/**
 * Front-end bridge for the conversational chatbot. Owns:
 *  - panel open/close state,
 *  - the active conversation id + live message stream,
 *  - the `chatAssistantSendMessage` callable.
 *
 * The service is workspace-scoped: switching workspace resets the
 * conversation. It only activates for owner-like workspaces — tenant /
 * service-provider / syndic catalogues land in later phases.
 */
@Injectable({ providedIn: 'root' })
export class AiAssistantService {
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService);
  private readonly productAnalytics = inject(ProductAnalyticsService);
  private readonly translate = inject(TranslateService);

  /** Interface language sent with every assistant call (server validates, default fr). */
  private uiLanguage(): string {
    return (this.translate.currentLang() || this.translate.getFallbackLang() || "fr").slice(0, 2);
  }

  private readonly _isOpen = signal(false);
  private readonly _conversationId = signal<string | null>(null);
  private readonly _messages = signal<readonly ChatMessage[]>([]);
  private readonly _sending = signal(false);
  private readonly _resolvingActionId = signal<string | null>(null);
  private readonly _lastError = signal<string | null>(null);
  private readonly _conversations = signal<readonly ConversationSummary[]>([]);
  private readonly _lastUserMessage = signal<{
    text: string;
    pageContext?: ChatPageContext;
    attachments?: ChatAttachmentRef[];
  } | null>(null);
  private readonly _temporaryMode = signal(false);
  private readonly _prefillDraft = signal<string | null>(null);
  private readonly _devUsage = signal<{ tokens: number; cost: string } | null>(null);

  readonly isOpen = this._isOpen.asReadonly();
  readonly conversationId = this._conversationId.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly sending = this._sending.asReadonly();
  readonly resolvingActionId = this._resolvingActionId.asReadonly();
  readonly lastError = this._lastError.asReadonly();
  readonly lastUserMessage = this._lastUserMessage.asReadonly();
  readonly temporaryMode = this._temporaryMode.asReadonly();
  /** Consumed by the panel composer when openWithPrefill was used. */
  readonly prefillDraft = this._prefillDraft.asReadonly();
  /** Dev-only usage rollup for today's AI spend (null in production builds). */
  readonly devUsage = this._devUsage.asReadonly();
  /**
   * Conversations visible in the history dropdown — temporaries are
   * hidden by default. The CURRENT conversation is always shown even
   * when temporary so the user can find their way back to it.
   */
  readonly conversations = computed<ConversationSummary[]>(() => {
    const all = this._conversations();
    const currentId = this._conversationId();
    return all.filter((c) => !c.temporary || c.id === currentId);
  });

  readonly isAvailable = computed(() => {
    if (!this.authStore.isAuthenticated()) return false;
    const ws = this.workspaceStore.currentWorkspace();
    return !!ws?.type && CHAT_ENABLED_WORKSPACE_TYPES.has(ws.type as WorkspaceType);
  });

  readonly hasMessages = computed(() => this._messages().length > 0);

  private unsubscribeMessages: Unsubscribe | null = null;
  /** Message ids whose invalid blocks were already logged (avoid re-logging per snapshot). */
  private readonly reportedInvalidBlocks = new Set<string>();
  private listeningTo: { workspaceId: string; conversationId: string } | null =
    null;
  private unsubscribeConversations: Unsubscribe | null = null;
  private conversationsListeningTo: string | null = null;

  constructor() {
    effect(() => {
      const wsId = this.workspaceStore.currentWorkspaceId();
      const convId = this._conversationId();
      if (!wsId || !convId) {
        this.detachMessageListener();
        return;
      }
      this.attachMessageListener(wsId, convId);
    });

    effect(() => {
      if (!this.authStore.isAuthenticated()) {
        this.reset();
      }
    });

    // Conversation list — refreshed on every workspace switch.
    effect(() => {
      const wsId = this.workspaceStore.currentWorkspaceId();
      if (!wsId) {
        this.detachConversationsListener();
        this._conversations.set([]);
        return;
      }
      this.attachConversationsListener(wsId);
    });

    this.destroyRef.onDestroy(() => {
      this.detachMessageListener();
      this.detachConversationsListener();
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', this.onGlobalShortcut);
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.onGlobalShortcut);
    }
  }

  /**
   * Ctrl+Shift+A (Win/Linux) / ⌘⇧A (Mac) — toggle assistant when available.
   * Bound once on the document; no-op when the catalogue is empty for the
   * current workspace (isAvailable=false).
   */
  private readonly onGlobalShortcut = (event: KeyboardEvent): void => {
    const key = event.key?.toLowerCase();
    if (key !== 'a') return;
    const mod = event.metaKey || event.ctrlKey;
    if (!mod || !event.shiftKey) return;
    // Ignore when typing in inputs outside our flow isn't needed — the
    // shortcut is intentional even in text fields for power users, but
    // skip contenteditable rich editors.
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('[contenteditable="true"]')) return;
    if (!this.isAvailable()) return;
    event.preventDefault();
    this.toggle();
  };

  open(): void {
    this._isOpen.set(true);
    this.trackOpened();
    void this.refreshDevUsage();
  }

  /**
   * Open the panel with a composer prefill (proactive alerts / toasts).
   */
  openWithPrefill(draft: string): void {
    this._prefillDraft.set(draft);
    this.open();
  }

  consumePrefill(): string | null {
    const v = this._prefillDraft();
    this._prefillDraft.set(null);
    return v;
  }

  close(): void {
    this._isOpen.set(false);
  }

  toggle(): void {
    const willOpen = !this._isOpen();
    this._isOpen.set(willOpen);
    if (willOpen) this.trackOpened();
  }

  /**
   * Product analytics — record that the assistant panel was opened, throttled
   * to once per day per workspace. Distinguishes "opened the assistant but
   * never sent a message" from "never opened it".
   */
  private trackOpened(): void {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) return;
    try {
      const key = `pa.assistant.opened.${workspaceId}`;
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(key) !== today) {
        localStorage.setItem(key, today);
        this.productAnalytics.track('assistant.opened');
      }
    } catch {
      /* localStorage unavailable — skip silently */
    }
  }

  /**
   * Forget the current conversation so the next message starts fresh.
   * The previous conversation stays in Firestore — we just stop tracking it.
   */
  startNewConversation(): void {
    this._conversationId.set(null);
    this._messages.set([]);
    this._lastError.set(null);
  }

  async sendMessage(
    text: string,
    pageContext?: ChatPageContext,
    options: {attachments?: ChatAttachmentRef[]} = {},
  ): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed && !options.attachments?.length) return;
    if (this._sending()) return;

    // Buffer the request so the UI can offer a "retry" affordance after
    // a failed turn without making the user re-type.
    this._lastUserMessage.set({
      text: trimmed,
      pageContext,
      attachments: options.attachments,
    });

    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      this._lastError.set('aiAssistant.errors.noWorkspace');
      return;
    }

    this._sending.set(true);
    this._lastError.set(null);

    const optimistic: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: new Date()
    };
    this._messages.update(current => [...current, optimistic]);

    // For a brand-new conversation, mint the id client-side and set it BEFORE
    // the call. Setting _conversationId attaches the messages listener (via the
    // constructor effect) up front, so the streamed assistant reply is observed
    // live from the first token — not only after the callable returns. The
    // optimistic user bubble is preserved across the listener's first (empty)
    // snapshot by reconcileWithOptimistic().
    const isNewConversation = this._conversationId() === null;
    if (isNewConversation) {
      const newId = doc(
        collection(this.firestore, `workspaces/${workspaceId}/chatConversations`)
      ).id;
      this._conversationId.set(newId);
    }

    try {
      const callable = this.createSendMessageCallable();
      const payload: SendMessageRequest = {
        workspaceId,
        language: this.uiLanguage(),
        message: trimmed,
        conversationId: this._conversationId() ?? undefined,
        pageContext,
        // Only honour the temporary flag when starting a fresh
        // conversation — flipping it mid-thread would be surprising.
        temporary: isNewConversation && this._temporaryMode() ? true : undefined,
        attachments: options.attachments,
      };
      const result = await callable(payload);
      const data = result.data;
      if (data?.conversationId && data.conversationId !== this._conversationId()) {
        this._conversationId.set(data.conversationId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.logger.error('chatAssistantSendMessage failed', 'AiAssistantService', { err: message });
      this._lastError.set(message);
      this._messages.update(current => current.filter(m => m.id !== optimistic.id));
    } finally {
      this._sending.set(false);
    }
  }

  async confirmAction(actionId: string): Promise<void> {
    await this.resolveAction(actionId, 'confirm');
  }

  async rejectAction(actionId: string): Promise<void> {
    await this.resolveAction(actionId, 'reject');
  }

  /**
   * Stage a mutate tool as a Confirm card without calling the LLM
   * (one-click widget CTAs).
   */
  async proposeAction(
    toolName: string,
    args: Record<string, unknown>
  ): Promise<void> {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      this._lastError.set('aiAssistant.errors.noWorkspace');
      return;
    }
    this._sending.set(true);
    this._lastError.set(null);
    try {
      const callable = this.createProposeActionCallable();
      const result = await callable({
        workspaceId,
        language: this.uiLanguage(),
        conversationId: this._conversationId() ?? undefined,
        toolName,
        args
      });
      const data = result.data;
      if (data?.conversationId) {
        this._conversationId.set(data.conversationId);
      }
      this.open();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.logger.error('chatAssistantProposeAction failed', 'AiAssistantService', {
        err: message
      });
      this._lastError.set(message);
    } finally {
      this._sending.set(false);
    }
  }

  /**
   * Persist a 👍/👎 on an assistant message (dual-write via callable).
   */
  async submitFeedback(args: {
    messageId: string;
    rating: 'up' | 'down';
    reasons?: string[];
    comment?: string;
    messageExcerpt?: string;
    toolsUsed?: string[];
  }): Promise<boolean> {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    const conversationId = this._conversationId();
    if (!workspaceId || !conversationId) {
      this._lastError.set('aiAssistant.errors.noWorkspace');
      return false;
    }
    try {
      const callable = httpsCallable<
        Record<string, unknown>,
        { feedbackId: string }
      >(this.functions, 'chatAssistantSubmitFeedback');
      await callable({
        workspaceId,
        conversationId,
        messageId: args.messageId,
        rating: args.rating,
        reasons: args.reasons,
        comment: args.comment,
        messageExcerpt: args.messageExcerpt,
        toolsUsed: args.toolsUsed
      });
      return true;
    } catch (err) {
      // The server keeps one feedback per (conversation, message, user): a
      // second submit is already recorded, which is the state the user wants.
      if ((err as { code?: string }).code === 'functions/already-exists') {
        return true;
      }
      const message = err instanceof Error ? err.message : String(err);
      this.logger.error('chatAssistantSubmitFeedback failed', 'AiAssistantService', {
        err: message
      });
      this._lastError.set(message);
      return false;
    }
  }

  private async resolveAction(
    actionId: string,
    decision: 'confirm' | 'reject'
  ): Promise<void> {
    if (this._resolvingActionId()) return;
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      this._lastError.set('aiAssistant.errors.noWorkspace');
      return;
    }
    this._resolvingActionId.set(actionId);
    this._lastError.set(null);
    try {
      const callable = this.createConfirmActionCallable();
      const result = await callable({workspaceId, actionId, decision, language: this.uiLanguage()});
      this.applyActionResult(actionId, result.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.logger.error(
        'chatAssistantConfirmAction failed',
        'AiAssistantService',
        {err: message}
      );
      this._lastError.set(message);
    } finally {
      this._resolvingActionId.set(null);
    }
  }

  /**
   * Patch the matching message's pendingAction with the resolved status,
   * so the card flips to "Confirmed" / "Rejected" / etc. without waiting
   * for the Firestore listener to round-trip.
   */
  private applyActionResult(
    actionId: string,
    response: ConfirmActionResponse | undefined
  ): void {
    if (!response) return;
    this._messages.update(messages =>
      messages.map(message => {
        if (!message.pendingActions?.length) return message;
        const idx = message.pendingActions.findIndex(a => a.actionId === actionId);
        if (idx < 0) return message;
        const next = [...message.pendingActions];
        next[idx] = {...next[idx], status: response.status as PendingActionStatus};
        return {...message, pendingActions: next};
      })
    );
  }

  private attachMessageListener(workspaceId: string, conversationId: string): void {
    if (
      this.listeningTo?.workspaceId === workspaceId &&
      this.listeningTo.conversationId === conversationId
    ) {
      return;
    }
    this.detachMessageListener();
    this.unsubscribeMessages = this.subscribeToMessages(
      workspaceId,
      conversationId,
      messages => this._messages.set(this.reconcileWithOptimistic(messages)),
      err => {
        this.logger.warn('chatAssistant: messages listener error', 'AiAssistantService', {
          err: err.message
        });
        this._lastError.set(err.message);
      }
    );
    this.listeningTo = { workspaceId, conversationId };
  }

  /**
   * Keep any client-only optimistic user bubble visible until its persisted
   * twin shows up in Firestore. Without this, the listener's first (empty)
   * snapshot on a freshly-created conversation would wipe the message the user
   * just sent, causing a visible blink before the backend persists it.
   */
  private reconcileWithOptimistic(persisted: ChatMessage[]): ChatMessage[] {
    const pending = this._messages().filter(m => m.id.startsWith('optimistic-'));
    if (pending.length === 0) return persisted;
    const persistedUserContents = new Set(
      persisted.filter(m => m.role === 'user').map(m => m.content)
    );
    const stillPending = pending.filter(m => !persistedUserContents.has(m.content));
    return stillPending.length > 0 ? [...persisted, ...stillPending] : persisted;
  }

  /**
   * Indirection so tests can stub the Firestore snapshot stream.
   */
  protected subscribeToMessages(
    workspaceId: string,
    conversationId: string,
    onMessages: (messages: ChatMessage[]) => void,
    onError: (err: Error) => void
  ): Unsubscribe {
    const messagesRef = collection(
      this.firestore,
      `workspaces/${workspaceId}/chatConversations/${conversationId}/messages`
    );
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    return onSnapshot(
      q,
      snapshot => {
        const next: ChatMessage[] = snapshot.docs.map(d => {
          const data = d.data() as Record<string, unknown>;
          const rawActions = data['pendingActions'];
          const pendingActions = Array.isArray(rawActions)
            ? (rawActions as PendingActionEmbed[]).map(a => ({...a}))
            : undefined;
          // Shape-validate instead of blind-casting: one malformed widget
          // must not crash (or silently blank) the whole message.
          const {blocks: richContent, rejected} = sanitizeRichContentBlocks(
            data['richContent']
          );
          if (rejected.length > 0 && !this.reportedInvalidBlocks.has(d.id)) {
            this.reportedInvalidBlocks.add(d.id);
            this.logger.warn(
              'chatAssistant: dropped unrenderable rich-content blocks',
              'AiAssistantService',
              {messageId: d.id, kinds: rejected}
            );
          }
          return {
            id: d.id,
            role: (data['role'] as ChatMessageRole) ?? 'assistant',
            content: (data['content'] as string) ?? '',
            createdAt: toDate(data['createdAt']),
            modelUsed: data['modelUsed'] as string | undefined,
            toolsUsed: data['toolsUsed'] as string[] | undefined,
            pendingActions,
            richContent,
            streaming: data['streaming'] === true,
            toolsInProgress: Array.isArray(data['toolsInProgress'])
              ? (data['toolsInProgress'] as unknown[]).filter(
                  (t): t is string => typeof t === 'string'
                )
              : undefined
          };
        });
        onMessages(next);
      },
      err => onError(err instanceof Error ? err : new Error(String(err)))
    );
  }

  private detachMessageListener(): void {
    this.unsubscribeMessages?.();
    this.unsubscribeMessages = null;
    this.listeningTo = null;
  }

  private attachConversationsListener(workspaceId: string): void {
    if (this.conversationsListeningTo === workspaceId) return;
    this.detachConversationsListener();
    const ref = collection(this.firestore, `workspaces/${workspaceId}/chatConversations`);
    const q = query(ref, orderBy('lastMessageAt', 'desc'), limit(30));
    this.unsubscribeConversations = onSnapshot(
      q,
      (snap) => {
        const list: ConversationSummary[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            title: (data['title'] as string | null | undefined) ?? null,
            firstUserMessage: (data['firstUserMessage'] as string | null | undefined) ?? null,
            lastMessageAt: toDate(data['lastMessageAt']),
            updatedAt: toDate(data['updatedAt']),
            pinned: data['pinned'] === true,
            temporary: data['temporary'] === true
          };
        });
        this._conversations.set(list);
      },
      (err) => {
        // permission-denied during onboarding / auth-claims propagation is
        // expected (the workspace exists but membership claims haven't landed
        // yet) and self-heals on the next workspace emission — log at debug so
        // it doesn't surface as a scary warning. See workspace.service.ts.
        if (isPermissionDeniedError(err)) {
          this.logger.debug('chatAssistant: conversations listener permission-denied (transient)', 'AiAssistantService');
          return;
        }
        this.logger.warn('chatAssistant: conversations listener error', 'AiAssistantService', {
          err: err.message
        });
      }
    );
    this.conversationsListeningTo = workspaceId;
  }

  private detachConversationsListener(): void {
    this.unsubscribeConversations?.();
    this.unsubscribeConversations = null;
    this.conversationsListeningTo = null;
  }

  /**
   * Switch the panel to an existing conversation. Triggers the message
   * listener via the `_conversationId` effect.
   */
  switchToConversation(conversationId: string): void {
    if (!conversationId) return;
    if (conversationId === this._conversationId()) return;
    this._conversationId.set(conversationId);
    this._messages.set([]);
    this._lastError.set(null);
  }

  /**
   * Re-send the last user message — used by the "retry" affordance on
   * a failed turn. No-op if no last message is buffered.
   */
  async retryLastMessage(): Promise<void> {
    const last = this._lastUserMessage();
    if (!last) return;
    await this.sendMessage(last.text, last.pageContext, {
      attachments: last.attachments,
    });
  }

  /**
   * Toggle the "temporary" mode for the NEXT new conversation. Has no
   * effect on the current conversation once it's been created — temp
   * is set at conversation-creation time on the server.
   */
  setTemporaryMode(value: boolean): void {
    this._temporaryMode.set(value);
  }

  /**
   * Permanently remove a conversation (and its messages subcollection)
   * server-side. The active conversation is reset if it was the deleted
   * one — a fresh thread will be opened on the next message.
   */
  async deleteConversation(conversationId: string): Promise<void> {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) return;
    const callable = this.createDeleteConversationCallable();
    try {
      await callable({workspaceId, conversationId});
      if (this._conversationId() === conversationId) {
        this.startNewConversation();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.logger.error('chatAssistantDeleteConversation failed', 'AiAssistantService', {
        err: message
      });
      this._lastError.set(message);
    }
  }

  protected createDeleteConversationCallable() {
    const callable = httpsCallable<
      {method: string; payload: {workspaceId: string; conversationId: string}},
      {conversationId: string; deleted: true; messagesDeleted: number}
    >(this.functions, 'aiAgentRouter');
    return (payload: {workspaceId: string; conversationId: string}) =>
      callable({method: 'chatAssistantDeleteConversation', payload});
  }

  /**
   * Indirection so tests can swap the callable without monkey-patching the
   * frozen `@angular/fire/functions` module re-exports.
   *
   * All three chat callables are now fronted by the single provisioned
   * `aiAgentRouter` (one warm instance for the whole agent surface), addressed
   * via the `{method, payload}` envelope. The returned function keeps the exact
   * `(payload) => Promise<{data}>` signature, so call sites — and the test seam
   * above — are unchanged.
   */
  protected createSendMessageCallable() {
    const callable = httpsCallable<
      {method: string; payload: SendMessageRequest},
      SendMessageResponse
    >(this.functions, 'aiAgentRouter');
    return (payload: SendMessageRequest) =>
      callable({method: 'chatAssistantSendMessage', payload});
  }

  protected createConfirmActionCallable() {
    const callable = httpsCallable<
      {method: string; payload: ConfirmActionRequest},
      ConfirmActionResponse
    >(this.functions, 'aiAgentRouter');
    return (payload: ConfirmActionRequest) =>
      callable({method: 'chatAssistantConfirmAction', payload});
  }

  protected createProposeActionCallable() {
    const callable = httpsCallable<
      {
        method: string;
        payload: {
          workspaceId: string;
          language?: string;
          conversationId?: string;
          toolName: string;
          args: Record<string, unknown>;
        };
      },
      {
        conversationId: string;
        messageId: string;
        actionId: string;
        summary: string;
      }
    >(this.functions, 'aiAgentRouter');
    return (payload: {
      workspaceId: string;
      language?: string;
      conversationId?: string;
      toolName: string;
      args: Record<string, unknown>;
    }) => callable({method: 'chatAssistantProposeAction', payload});
  }

  /** Dev-only: read today's aiUsageDaily rollup for the panel header. */
  private async refreshDevUsage(): Promise<void> {
    if (environment.production) {
      this._devUsage.set(null);
      return;
    }
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      const snap = await getDoc(
        doc(this.firestore, `workspaces/${workspaceId}/aiUsageDaily/${today}`)
      );
      if (!snap.exists()) {
        this._devUsage.set({ tokens: 0, cost: '$0.00' });
        return;
      }
      const d = snap.data() as {
        totalInputTokens?: number;
        totalOutputTokens?: number;
        totalUsd?: number;
      };
      const tokens =
        (d.totalInputTokens ?? 0) + (d.totalOutputTokens ?? 0);
      const cost = `$${(d.totalUsd ?? 0).toFixed(2)}`;
      this._devUsage.set({ tokens, cost });
    } catch (err) {
      this.logger.warn('dev usage rollup unavailable', 'AiAssistantService', {
        err: err instanceof Error ? err.message : String(err)
      });
    }
  }

  private reset(): void {
    this.detachMessageListener();
    this.detachConversationsListener();
    this._isOpen.set(false);
    this._conversationId.set(null);
    this._messages.set([]);
    this._sending.set(false);
    this._lastError.set(null);
    this._conversations.set([]);
    this._lastUserMessage.set(null);
    this._temporaryMode.set(false);
  }
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (
    typeof value === 'object' &&
    value &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}
